import type { FC, ReactNode, RefObject } from "react";
import type { Placement } from "react-aria";
import { Provider, TooltipTriggerStateContext } from "react-aria-components";
import { useTooltipTriggerState } from "react-stately";
import { SimpleTooltip } from "./simpleTooltip.js";

export interface AnchoredTooltipProps {
  /** The element to position against. */
  triggerRef: RefObject<Element | null>;
  placement?: Placement;
  children?: ReactNode;
}

/**
 * Tooltip positioned against an arbitrary element, shown for as long as it is
 * rendered.
 *
 * `TooltipTrigger` needs a focusable React Aria component as its trigger, which
 * rules out the shapes in a hand-drawn SVG chart. Point this at the hovered
 * shape instead and render it only while that shape is hovered — the hover
 * handlers and the "which one is hovered" state are the caller's business.
 */
export const AnchoredTooltip: FC<AnchoredTooltipProps> = (props) => {
  // There is no trigger to open it, so force it open.
  const state = useTooltipTriggerState({ isOpen: true });

  return (
    <Provider values={[[TooltipTriggerStateContext, state]]}>
      <SimpleTooltip triggerRef={props.triggerRef} placement={props.placement}>
        {props.children}
      </SimpleTooltip>
    </Provider>
  );
};
